import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Table, Modal } from "react-bootstrap";
import { FaTrash, FaPlus, FaExternalLinkAlt, FaExpand, FaCopy, FaMapMarkerAlt, FaSatellite } from "react-icons/fa"; // Added FaSatellite
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import Wrapper from "../../components/Wrapper";
import { fetchUserFarms, deleteFarm, resetFarmsState } from "../../store/features/userFarmsSlice";
import { hasPermission } from "../../hooks/usePermission";
import { hasUsableBoundary } from "../../Pages/Support/FarmSatellite/satelliteModel"; // Added import
import "../../styles/reports.css";

// ── Auto fit-bounds once map is ready ─────────────────────────────────────────
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length < 3) return;
    const lats = positions.map(p => p[0]);
    const lngs = positions.map(p => p[1]);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
      { padding: [12, 12] }
    );
  }, []); // eslint-disable-line
  return null;
}

// ── Static thumbnail (small non-interactive Leaflet map) ─────────────────────
const FarmMapThumb = ({ points, onOpen }) => {
  if (!points || points.length < 3) return <span className="text-muted">N/A</span>;

  const positions = points.map(p => [p.latitude ?? p.lat, p.longitude ?? p.lng]);
  const center = [
    positions.reduce((s, p) => s + p[0], 0) / positions.length,
    positions.reduce((s, p) => s + p[1], 0) / positions.length,
  ];

  return (
    <button
      onClick={onOpen}
      title="Click to view farm boundary on map"
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', position: 'relative', display: 'inline-block' }}
    >
      <div style={{ width: 130, height: 85, borderRadius: 6, overflow: 'hidden', border: '2px solid #113460', pointerEvents: 'none' }}>
        <MapContainer
          center={center}
          zoom={14}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={false}
          dragging={false}
          zoomControl={false}
          attributionControl={false}
          keyboard={false}
          doubleClickZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          <Polygon
            positions={positions}
            pathOptions={{ fillColor: '#199ee0', fillOpacity: 0.25, color: '#113460', weight: 2 }}
          />
          <FitBounds positions={positions} />
        </MapContainer>
      </div>
      <span style={{
        position: 'absolute', bottom: 4, right: 4,
        background: 'rgba(0,0,0,0.55)', borderRadius: 4,
        padding: '2px 4px', color: '#fff', fontSize: 10,
        display: 'flex', alignItems: 'center', gap: 3,
      }}>
        <FaExpand size={9} /> View
      </span>
    </button>
  );
};

// ── Full interactive map modal (read-only polygon) ────────────────────────────
const FarmMapModal = ({ farm, onClose }) => {
  const points    = farm.geo_location?.data || [];
  const positions = points
    .map(p => [p.latitude ?? p.lat, p.longitude ?? p.lng])
    .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

  const center = positions.length > 0
    ? [
        positions.reduce((s, p) => s + p[0], 0) / positions.length,
        positions.reduce((s, p) => s + p[1], 0) / positions.length,
      ]
    : [20.5937, 78.9629];

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 12 }}>
        <div>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {farm.farm_name}
          </Modal.Title>
          {farm.farm_size && (
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
              {farm.farm_size} {farm.farm_size_unit || ''} · Farm Boundary (View Only)
            </p>
          )}
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: 0, borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        <MapContainer
          center={center}
          zoom={14}
          style={{ width: '100%', height: 460 }}
          scrollWheelZoom
          zoomControl
        >
          {/* Satellite tiles (Esri) — no API key needed */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri"
            maxNativeZoom={18}
            maxZoom={22}
          />
          {positions.length >= 3 && (
            <>
              <Polygon
                positions={positions}
                pathOptions={{
                  fillColor:    '#199ee0',
                  fillOpacity:  0.25,
                  color:        '#113460',
                  weight:       2,
                  opacity:      1,
                }}
              />
              <FitBounds positions={positions} />
            </>
          )}
        </MapContainer>
      </Modal.Body>
    </Modal>
  );
};

const UserFarms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Added permission check
  const canViewSatellite = hasPermission("farms:satellite");

  const { farms, userData, status } = useSelector((state) => state.userFarms);
  const [viewingFarm, setViewingFarm] = useState(null);
  const [copiedFarmId, setCopiedFarmId] = useState(null);

  const getFarmCenter = (farm) => {
    const points = farm.geo_location?.data;
    if (!points || points.length === 0) return null;
    const lats = points.map(p => p.latitude ?? p.lat).filter(v => v != null && !isNaN(v));
    const lngs = points.map(p => p.longitude ?? p.lng).filter(v => v != null && !isNaN(v));
    if (lats.length === 0) return null;
    return {
      lat: (lats.reduce((a, b) => a + b, 0) / lats.length).toFixed(6),
      lng: (lngs.reduce((a, b) => a + b, 0) / lngs.length).toFixed(6),
    };
  };

  const handleCopyCoords = (farm) => {
    const center = getFarmCenter(farm);
    if (!center) return;
    navigator.clipboard.writeText(`${center.lat}, ${center.lng}`).then(() => {
      setCopiedFarmId(farm.farm_id);
      setTimeout(() => setCopiedFarmId(null), 2000);
    });
  };

  useEffect(() => {
    dispatch(fetchUserFarms({ id }));
    return () => {
        dispatch(resetFarmsState());
    };
  }, [dispatch, id]);

  const handleDelete = async (farmId) => {
    if (window.confirm("Are you sure you want to delete this farm?")) {
      dispatch(deleteFarm({ id: farmId }));
    }
  };

  return (
    <Wrapper>
      <div className="reports-container py-4">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-3 w-100">
          <div>
            <h1 className="fw-bolder">Farms</h1>
            {userData && (
              <p className="text-muted mb-0">
                Farmer: {userData.first_name} {userData.last_name}
              </p>
            )}
          </div>

          <div className="d-flex justify-content-end align-items-center mt-3 mt-md-0">
            {hasPermission('farms:add') && (
              <Button
                variant="primary"
                onClick={() => navigate(`/farmers/create-farms/${id}`)}
              >
                <FaPlus className="me-2" /> Add Farm
              </Button>
            )}
          </div>
        </div>

        {status === "loading" ? (
          <h4 className="text-center mt-5">Loading...</h4>
        ) : farms.length > 0 ? (
          <div className="table-responsive">
            <Table striped bordered hover className="align-middle text-center">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Farm Name</th>
                  <th>Address</th>
                  <th>Size</th>
                  <th>GPS Location</th>
                  <th>Geo-Fence</th>
                  <th>Live Update</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {farms.map((farm, index) => (
                  <tr key={farm.farm_id || index}>
                    <td>{index + 1}</td>
                    <td className="fw-bold">{farm.farm_name}</td>
                    <td>{farm.address || '-'}</td>
                    <td>
                      {farm.farm_size 
                        ? `${farm.farm_size} ${farm.farm_size_unit || ''}` 
                        : '-'}
                    </td>
                    <td style={{ minWidth: 160 }}>
                      {(() => {
                        const center = getFarmCenter(farm);
                        if (!center) return <span className="text-muted">-</span>;
                        const coordsText = `${center.lat}, ${center.lng}`;
                        return (
                          <div style={{ fontSize: 12 }}>
                            <div style={{ fontFamily: 'monospace', marginBottom: 4 }}>
                              <div><span style={{ color: '#6b7280' }}>Lat:</span> {center.lat}</div>
                              <div><span style={{ color: '#6b7280' }}>Lng:</span> {center.lng}</div>
                            </div>
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                onClick={() => handleCopyCoords(farm)}
                                title="Copy coordinates"
                                style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11, color: copiedFarmId === farm.farm_id ? '#16a34a' : '#374151' }}
                              >
                                <FaCopy size={10} className="me-1" />
                                {copiedFarmId === farm.farm_id ? 'Copied!' : 'Copy'}
                              </button>
                              <a
                                href={`https://www.google.com/maps?q=${coordsText}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open in Google Maps"
                                style={{ border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: '#1d4ed8', textDecoration: 'none', background: '#eff6ff' }}
                              >
                                <FaMapMarkerAlt size={10} className="me-1" />
                                Maps
                              </a>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <FarmMapThumb
                        points={farm.geo_location?.data}
                        onOpen={() => setViewingFarm(farm)}
                      />
                      {/* Added Satellite Button Check here */}
                      {canViewSatellite && hasUsableBoundary(farm) && (
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-center mx-auto mt-1 gap-1"
                          onClick={() => navigate(`/farms/${encodeURIComponent(farm.farm_id)}/satellite`, { state: { farm } })}
                          title="View satellite insights"
                        >
                          <FaSatellite size={12} />
                          <span>Satellite</span>
                        </button>
                      )}
                    </td>
                    <td>
                      {farm.satyukt_farm_id ? (
                        <a 
                          href={`/users/farm-dashboard/${id}/${farm.satyukt_farm_id}`}
                          className="btn btn-sm btn-outline-success"
                        >
                          <FaExternalLinkAlt className="me-1"/> View Dashboard
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                        <Button 
                            variant="danger" 
                            size="sm"
                            onClick={() => handleDelete(farm.farm_id)}
                        >
                            <FaTrash />
                        </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center mt-5">
            <h3>No Farms Listed</h3>
          </div>
        )}
      </div>

      {/* Farm boundary map modal */}
      {viewingFarm && (
        <FarmMapModal
          farm={viewingFarm}
          onClose={() => setViewingFarm(null)}
        />
      )}
    </Wrapper>
  );
};

export default UserFarms;